/**
 * 入住选城（UI-MOVEIN-CITY-01，后端 CITY-01；用户 10-08「入住城市放开选」）：家第一次入住、还没定过地方时，主人用三种方式之一选一座城——
 * 搜一座城（GET /cities/search，只认中文名；城市名里搜不到时给区县别名「昆山属于苏州」）、按生活环境找（GET /cities/recommend）、
 * 随便看看（GET /cities/random）；后两种一批 3 个，可以换一批，点「入住」才算数。没有默认城市：不选不能入住（后端 city_required）。
 * 选中后读这座城此刻的实况（GET /cities/{city_id}），照字段如实说：住了几只宠物、有没有邻居、人少就提醒；没开的城地址要晚一点落定（落定前 TA 不出门），
 * 开城停在哪、最近一座已开的城是哪座。核不过就照实说，不悄悄换城。选地方不调模型、不生图；三种方式都是主人点了才去读。
 */
import { useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { HabitatKindValues, type CityCard, type CityStatus, type HabitatKind, type PickCard } from "@/shared/contracts";
import { isApiError } from "@/shared/api/errors";
import { useServices } from "@/shared/services/registry";
import { Button, Chip, ErrorState, Icon, LoadingState } from "@/shared/ui";

/** 选中的一座城：入住时带 city_id 和生活环境。 */
export type CityPick = { cityId: string; name: string; habitat: HabitatKind };
type Mode = "search" | "recommend" | "random";

/** 生活环境的说法，和后端 web_home/place.py 的 HABITATS 同一套字。 */
export const HABITAT_LABEL: Record<HabitatKind, string> = { seaside: "海边", grassland: "草原", desert: "沙漠", forest: "森林", lakeside: "湖边", mountain: "山里", city: "城市", countryside: "田园" };

/**
 * 城市卡上那一类环境的图（只是这一类地方的样子，不是这座城的实景）：
 * - 海边、城市：UI-ASSET-001 v1（r7k 交付，c84a 素材检查通过）：海边 SHA-256 3709438F…/blob 1fa57117，城市 3487B7C8…/blob 23b3f6a2；
 *   卡上用 I 10-13 从这两张导出的 144×180 缩略图（v1/thumb/，出处、指纹见 thumb/SOURCE.md，UI-MOVEIN-CITY-01b）；原图照旧留着，别处的大图位还在用；
 * - 草原、沙漠、森林、湖边、山里、田园：ASSET-UI-021 v1（r7k 交付，I 10-09 审过六张，接入点就是这一页；原字节、指纹、出处见 public/ui-assets/ASSET-UI-021/v1/SOURCE.md）。
 *   卡上只显示 72px 见方，用的是 I 10-13 从这六张导出的 144×180 缩略图（v1/thumb/，2 倍屏够清楚；出处、指纹见 thumb/SOURCE.md，UI-MOVEIN-CITY-01b），原图留在 v1/ 作底本。
 * 图打不开时显示中性色块加线性记号，不用示意画冒充。
 */
const HABITAT_ART: Record<HabitatKind, string> = {
  seaside: "/ui-assets/UI-ASSET-001/v1/thumb/habitat-seaside.webp",
  city: "/ui-assets/UI-ASSET-001/v1/thumb/habitat-city.webp",
  grassland: "/ui-assets/ASSET-UI-021/v1/thumb/habitat-grassland.webp",
  desert: "/ui-assets/ASSET-UI-021/v1/thumb/habitat-desert.webp",
  forest: "/ui-assets/ASSET-UI-021/v1/thumb/habitat-forest.webp",
  lakeside: "/ui-assets/ASSET-UI-021/v1/thumb/habitat-lakeside.webp",
  mountain: "/ui-assets/ASSET-UI-021/v1/thumb/habitat-mountain.webp",
  countryside: "/ui-assets/ASSET-UI-021/v1/thumb/habitat-countryside.webp",
};
const HABITAT_MARK: Partial<Record<HabitatKind, "wave" | "home" | "sprout" | "compass">> = { seaside: "wave", lakeside: "wave", city: "home", countryside: "sprout", grassland: "sprout", forest: "sprout" };

export function asHabitat(value: string | null | undefined): HabitatKind | null {
  return (HabitatKindValues as readonly string[]).includes(value ?? "") ? (value as HabitatKind) : null;
}

/** 这座城能选的生活环境（照接口给的顺序，去掉不认识的）；「城市」每座城都有，接口没给时也算有。 */
export function cityHabitats(city: Pick<CityCard, "habitats"> | null | undefined): HabitatKind[] {
  const known = (city?.habitats ?? []).map(asHabitat).filter((h): h is HabitatKind => h !== null);
  return known.length ? Array.from(new Set(known)) : ["city"];
}

/** 一座城的实况读出来以后，这座城还能不能入住：只有「暂不开放的地区」不能（入住会 422 region_unsupported；目录里没有这座城时状态读口 404 带同一个原因码）。 */
export function cityBlocked(status: CityStatus | undefined, error: unknown): boolean {
  if (status) return status.reason === "region_unsupported";
  return isApiError(error) && error.status === 404 && error.details?.reason === "region_unsupported";
}

/**
 * 照这座城此刻的实况说几句真话（只用接口给的字段，不编）：
 * 人：住了几只宠物（0 不写数字）、有没有邻居；两样都没有就提醒「人还很少」。
 * 地址：已开的城到了就能住进去，不多说；没开的城地址要晚一点落定、落定前 TA 不出门；开城这件事还没装上（opening_available=false）时只说说不准什么时候好，
 * 不报进度和排队；装上了就照原因码说停在哪。
 */
export function cityTruth(status: CityStatus): string[] {
  const { city } = status;
  const lines: string[] = [];
  if (city.pet_count) lines.push(`已经有 ${city.pet_count} 只宠物住在这里。`);
  if (city.npc_neighbors > 0) lines.push("这里有邻居。");
  if (!city.pet_count && city.npc_neighbors <= 0) lines.push("这里住的宠物还很少。");
  if (status.reason === "region_unsupported") {
    lines.push("这个地区暂时还没开放，换一座城吧。");
    return lines;
  }
  if (city.ready) return lines;
  if (!status.opening_available) {
    lines.push("这座城还在准备，暂时说不准什么时候好。新家地址落定之前，TA 先待在家里，不出门。");
    return lines;
  }
  lines.push("新家地址要晚一点落定。落定之前，TA 先待在家里，不出门。");
  if (status.reason === "quota_wait" && status.queue_position) lines.push(`这座城正在排队准备，排在第 ${status.queue_position} 位。`);
  else if (status.reason === "no_residential_found" || status.reason === "verify_failed") lines.push("上次没找到合适的住处，选了这里会再找一次。");
  else if (status.reason === "provider_error") lines.push("准备时遇到一点问题，会自动再试。");
  else if (status.step) lines.push("正在为这座城找合适的住处。");
  return lines;
}

/** 选中的那座城的实况；入住页和选城区同一份（同一个查询键，只读一次）。 */
export function useCityStatus(cityId: string | null | undefined) {
  const { session } = useServices();
  return useQuery({ queryKey: ["cities", "status", cityId ?? "-"], queryFn: () => session.cityStatus(cityId!), enabled: Boolean(cityId), retry: false });
}

function HabitatArt({ habitat }: { habitat: HabitatKind }) {
  const art = HABITAT_ART[habitat];
  const [failed, setFailed] = useState(false);
  return (
    <span className={`ps-city-card__art is-${habitat}`} aria-hidden="true">
      {art && !failed ? <img src={art} alt="" onError={() => setFailed(true)} /> : <Icon name={HABITAT_MARK[habitat] ?? "compass"} size={22} strokeWidth={1.5} />}
    </span>
  );
}

type CityTag = { tone: "leaf" | "sky" | "sun"; icon: "user" | "home" | "pin"; text: string };

/** 城市卡上的小标记：住了几只宠物（0 不出）、有邻居（0 不出）、没开的城「地址晚一点落定」。 */
function cityTags({ ready, petCount, neighbors }: { ready: boolean; petCount?: number | null; neighbors?: number }): CityTag[] {
  const tags: CityTag[] = [];
  if (petCount) tags.push({ tone: "leaf", icon: "user", text: `已有 ${petCount} 只宠物` });
  if (neighbors && neighbors > 0) tags.push({ tone: "sky", icon: "home", text: "有邻居" });
  if (!ready) tags.push({ tone: "sun", icon: "pin", text: "地址晚一点落定" });
  return tags;
}

/**
 * 一行城市（按钮，aria-pressed）：城市名、一句说明、几枚小标记；推荐和随机的卡带上那一类环境的图。
 * 读屏名字按「城市名，说明，标记」带停顿念（几段字直接连着念会成「苏州湖边地址晚一点落定」），画面上的字照旧。
 */
function CityOption({ selected, onSelect, art, title, note, tags = [] }: { selected: boolean; onSelect: () => void; art?: HabitatKind; title: string; note: string; tags?: CityTag[] }) {
  return (
    <button type="button" className={`ps-city-card${selected ? " is-selected" : ""}${art ? "" : " is-plain"}`} aria-pressed={selected} aria-label={[title, note, ...tags.map((t) => t.text)].join("，")} onClick={onSelect}>
      {art ? <HabitatArt habitat={art} /> : null}
      <span className="ps-city-card__text">
        <strong>{title}</strong>
        <small>{note}</small>
        {tags.length ? <span className="ps-city-card__tags">{tags.map((t) => <Chip key={t.text} tone={t.tone} icon={t.icon}>{t.text}</Chip>)}</span> : null}
      </span>
      <span className="ps-city-card__check" aria-hidden="true"><Icon name="check" size={15} strokeWidth={2.6} /></span>
    </button>
  );
}

/** 城市卡上那一句：只有城市生活时写「这里目前是城市生活」，不止一类就照接口给的顺序列出来。 */
export function habitatNote(city: Pick<CityCard, "habitats">): string {
  const habitats = cityHabitats(city);
  return habitats.length === 1 && habitats[0] === "city" ? "这里目前是城市生活" : habitats.map((h) => HABITAT_LABEL[h]).join("、");
}

function SearchPanel({ pick, onPick }: { pick: CityPick | null; onPick: (pick: CityPick) => void }) {
  const { session } = useServices();
  const [draft, setDraft] = useState("");
  const [query, setQuery] = useState("");
  const found = useQuery({ queryKey: ["cities", "search", query], queryFn: () => session.searchCities(query), enabled: query !== "" });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setQuery(draft.trim().slice(0, 16));
  };
  const data = found.data;
  return (
    <div className="ps-city-panel">
      <form className="ps-city-search" role="search" aria-label="搜一座城" onSubmit={submit}>
        <input className="ps-input" type="search" aria-label="城市名" placeholder="输入城市的中文名，比如郑州" maxLength={16} enterKeyHint="search" value={draft} onChange={(e) => setDraft(e.target.value)} />
        <Button type="submit" variant="secondary" disabled={!draft.trim()}>搜索</Button>
      </form>
      {query === "" ? null : found.isPending ? (
        <LoadingState lines={1} label="正在找这座城…" />
      ) : found.isError ? (
        <ErrorState error={found.error} onRetry={() => void found.refetch()} />
      ) : data && data.cities.length ? (
        <div className="ps-city-list" role="group" aria-label="搜到的城市">
          {data.cities.map((city) => (
            <CityOption
              key={city.city_id}
              selected={pick?.cityId === city.city_id}
              onSelect={() => onPick({ cityId: city.city_id, name: city.short_name, habitat: "city" })}
              title={city.province && city.province !== city.short_name ? `${city.short_name} · ${city.province}` : city.short_name}
              note={habitatNote(city)}
              tags={cityTags({ ready: city.ready, petCount: city.pet_count, neighbors: city.npc_neighbors })}
            />
          ))}
        </div>
      ) : data && data.alias_of.length ? (
        <div className="ps-city-list" role="group" aria-label="搜到的地方属于这些城市">
          {data.alias_of.map((alias) => (
            <CityOption
              key={`${alias.name}-${alias.city_id}`}
              selected={pick?.cityId === alias.city_id}
              onSelect={() => onPick({ cityId: alias.city_id, name: alias.short_name, habitat: "city" })}
              title={alias.short_name}
              note={`${alias.name}属于${alias.short_name}，可以选${alias.short_name}`}
            />
          ))}
        </div>
      ) : (
        <p className="ps-city-empty">没有找到「{query}」。换个名字试试，或者按生活环境找、随便看看。</p>
      )}
    </div>
  );
}

/** 推荐与随机：一批 3 个，「换一批」往后翻（服务端按这个家固定顺序，翻完从头来，用完之前不重复）。 */
function PickPanel({ mode, petId, pick, onPick }: { mode: "recommend" | "random"; petId: string | null; pick: CityPick | null; onPick: (pick: CityPick) => void }) {
  const { session } = useServices();
  const [habitat, setHabitat] = useState<HabitatKind | null>(null);
  const [batch, setBatch] = useState(0);
  const cards = useQuery({
    queryKey: mode === "recommend" ? ["cities", "recommend", petId ?? "-", habitat ?? "any", batch] : ["cities", "random", petId ?? "-", batch],
    queryFn: () => (mode === "recommend" ? session.recommendCities(habitat, batch, petId) : session.randomCities(batch, petId)),
  });
  const list = (cards.data?.cards ?? []).map((card) => ({ card, habitat: asHabitat(card.habitat) ?? "city", key: `${card.city_id}-${card.habitat}` }));
  // 选中的是哪一张：同一座城又同一类的那张；在这座城里改了生活环境、这一批里没有那一类的卡时，勾留在这座城的卡上（城没换）
  const chosen = pick ? (list.find((x) => x.card.city_id === pick.cityId && x.habitat === pick.habitat) ?? list.find((x) => x.card.city_id === pick.cityId)) : undefined;
  const choose = (h: HabitatKind | null) => {
    setHabitat(h);
    setBatch(0);
  };
  return (
    <div className="ps-city-panel">
      {mode === "recommend" ? (
        <div className="ps-city-choices__list" role="group" aria-label="喜欢的生活环境">
          <button type="button" className={`ps-city-chip${habitat === null ? " is-selected" : ""}`} aria-pressed={habitat === null} onClick={() => choose(null)}>都可以</button>
          {HabitatKindValues.map((h) => (
            <button key={h} type="button" className={`ps-city-chip${habitat === h ? " is-selected" : ""}`} aria-pressed={habitat === h} onClick={() => choose(h)}>{HABITAT_LABEL[h]}</button>
          ))}
        </div>
      ) : null}
      {cards.isPending ? (
        <LoadingState lines={2} label="正在挑几座城…" />
      ) : cards.isError ? (
        <ErrorState error={cards.error} onRetry={() => void cards.refetch()} />
      ) : list.length ? (
        <>
          <div className="ps-city-list" role="group" aria-label={mode === "recommend" ? "推荐的城市" : "随便看看的城市"}>
            {list.map(({ card, habitat: h, key }) => (
              <CityPickOption key={key} card={card} habitat={h} selected={chosen?.key === key} onPick={onPick} />
            ))}
          </div>
          <Button className="ps-city-more" variant="secondary" icon="refresh" onClick={() => setBatch((n) => n + 1)}>换一批</Button>
        </>
      ) : (
        <p className="ps-city-empty">{mode === "recommend" ? "这一类地方暂时没有可以推荐的城市。换一类看看，或者搜一座城。" : "暂时挑不出城市。可以搜一座城。"}</p>
      )}
    </div>
  );
}

function CityPickOption({ card, habitat, selected, onPick }: { card: PickCard; habitat: HabitatKind; selected: boolean; onPick: (pick: CityPick) => void }) {
  return (
    <CityOption
      selected={selected}
      onSelect={() => onPick({ cityId: card.city_id, name: card.short_name, habitat })}
      art={habitat}
      title={card.short_name}
      note={HABITAT_LABEL[habitat]}
      tags={cityTags({ ready: card.ready })}
    />
  );
}

const MODES: Array<{ id: Mode; label: string; icon: "pin" | "compass" | "sparkle"; hint: string }> = [
  { id: "search", label: "搜一座城", icon: "pin", hint: "心里已经有一个地方：输入它的名字。" },
  { id: "recommend", label: "按环境找", icon: "compass", hint: "从喜欢的生活环境开始，一次看 3 座城。" },
  { id: "random", label: "随便看看", icon: "sparkle", hint: "先遇见 3 座可能的城市，不喜欢就换一批。" },
];

/** 选中那座城的说明：哪座城、过什么样的生活（这座城有不止一类时可以换）、这座城此刻的实况，没开的城给最近一座已开的城。 */
function PickDetail({ pick, status, onPick }: { pick: CityPick; status: ReturnType<typeof useCityStatus>; onPick: (pick: CityPick) => void }) {
  const data = status.data;
  const habitats = data ? cityHabitats(data.city) : [];
  const near = data && !data.city.ready ? data.nearest_open : null;
  return (
    <div className="ps-city-detail" aria-live="polite">
      <p className="ps-city-detail__title">
        TA 会住在 <strong>{pick.name}</strong>
        {data?.city.province && data.city.province !== pick.name ? ` · ${data.city.province}` : ""}
      </p>
      {status.isPending ? (
        <LoadingState lines={1} label="正在看看这座城…" />
      ) : status.isError ? (
        <p className="ps-city-detail__line">{cityBlocked(undefined, status.error) ? "这个地区暂时还没开放，换一座城吧。" : "这座城的情况暂时读不到，入住时会再确认一次。"}</p>
      ) : data ? (
        <>
          {habitats.length > 1 ? (
            <div className="ps-city-choices">
              <p className="ps-city-choices__title">在{pick.name}想过什么样的生活？</p>
              <div className="ps-city-choices__list" role="group" aria-label="这座城的生活环境">
                {habitats.map((h) => (
                  <button key={h} type="button" className={`ps-city-chip${pick.habitat === h ? " is-selected" : ""}`} aria-pressed={pick.habitat === h} onClick={() => onPick({ ...pick, habitat: h })}>{HABITAT_LABEL[h]}</button>
                ))}
              </div>
            </div>
          ) : (
            <p className="ps-city-detail__line">这里目前是城市生活。</p>
          )}
          {cityTruth(data).map((line) => <p key={line} className="ps-city-detail__line">{line}</p>)}
          {near ? (
            <div className="ps-city-detail__near">
              <p className="ps-city-detail__line">离这里最近、已经能直接住的是{near.short_name}（约 {Math.max(1, Math.round(near.distance_km))} 公里）。</p>
              <Button variant="secondary" size="sm" onClick={() => onPick({ cityId: near.city_id, name: near.short_name, habitat: "city" })}>改选{near.short_name}</Button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

export function CityPicker({ petId, pick, onPick, status }: { petId: string | null; pick: CityPick | null; onPick: (pick: CityPick) => void; status: ReturnType<typeof useCityStatus> }) {
  const [mode, setMode] = useState<Mode | null>(null);
  const active = MODES.find((m) => m.id === mode) ?? null;
  return (
    <div className="ps-city-picker">
      <div className="ps-city-modes" role="group" aria-label="怎么选城">
        {MODES.map((m) => (
          <button key={m.id} type="button" className={`ps-city-mode${mode === m.id ? " is-selected" : ""}`} aria-pressed={mode === m.id} onClick={() => setMode(m.id)}>
            <Icon name={m.icon} size={20} strokeWidth={1.8} />
            <span>{m.label}</span>
          </button>
        ))}
      </div>
      <p className="ps-movein-note ps-city-hint">{active ? active.hint : "选一种方式，挑一座城。"}</p>
      {mode === "search" ? <SearchPanel pick={pick} onPick={onPick} /> : null}
      {mode === "recommend" || mode === "random" ? <PickPanel key={mode} mode={mode} petId={petId} pick={pick} onPick={onPick} /> : null}
      {pick ? <PickDetail pick={pick} status={status} onPick={onPick} /> : null}
    </div>
  );
}
